import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { FileText, CheckCircle, AlertTriangle, Scale } from 'lucide-react';
import SEO from '../components/SEO';

const TermsOfService = () => {
  return (
    <div className="min-h-screen py-16">
      <SEO
        title="Conditions d'Utilisation"
        description="Conditions générales d'utilisation de la plateforme publicitaire Eau Gratuite."
        path="/conditions-utilisation"
      />
      {/* Header Section */}
      <section className="py-16 bg-gradient-to-br from-sky-50 via-white to-emerald-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-sky-400 to-emerald-400 rounded-xl mx-auto mb-6">
            <FileText className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Conditions d'Utilisation
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Modalités d'utilisation de la plateforme publicitaire Eau Gratuite
          </p>
          <p className="text-sm text-gray-500 mt-4">
            Dernière mise à jour : 12 septembre 2025
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            
            {/* Introduction */}
            <Card className="p-8 border-0 shadow-lg">
              <CardContent className="p-0">
                <div className="flex items-center space-x-3 mb-6">
                  <CheckCircle className="h-6 w-6 text-sky-600" />
                  <h2 className="text-2xl font-bold text-gray-900">
                    1. Présentation du Service
                  </h2>
                </div>
                <div className="space-y-4 text-gray-600">
                  <p>
                    Eau Gratuite est une plateforme publicitaire innovante qui permet aux entreprises 
                    de promouvoir leur marque sur des bouteilles d'eau distribuées gratuitement au public.
                  </p>
                  <p>
                    Une partie des revenus publicitaires finance la construction de puits d'eau potable 
                    via notre partenariat avec l'Association Ulker's.
                  </p>
                  <p>
                    L'utilisation de notre site web et de nos services implique l'acceptation pleine 
                    et entière des présentes conditions d'utilisation.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Services */}
            <Card className="p-8 border-0 shadow-lg">
              <CardContent className="p-0">
                <div className="flex items-center space-x-3 mb-6">
                  <Scale className="h-6 w-6 text-emerald-600" />
                  <h2 className="text-2xl font-bold text-gray-900">
                    2. Services Proposés
                  </h2>
                </div>
                <div className="space-y-4 text-gray-600">
                  <p>Nos services incluent :</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Campagnes publicitaires sur bouteilles d'eau recyclables</li>
                    <li>Distribution gratuite en Île-de-France</li>
                    <li>Rapports d'impact social et environnemental</li>
                    <li>Support client dédié pour les annonceurs</li>
                    <li>Financement de projets de puits d'eau via l'Association Ulker's</li>
                  </ul>
                  <p>
                    Les modalités spécifiques de chaque campagne sont définies dans des contrats 
                    commerciaux séparés conclus avec chaque annonceur.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Obligations annonceurs */}
            <Card className="p-8 border-0 shadow-lg">
              <CardContent className="p-0">
                <div className="flex items-center space-x-3 mb-6">
                  <AlertTriangle className="h-6 w-6 text-sky-600" />
                  <h2 className="text-2xl font-bold text-gray-900">
                    3. Obligations des Annonceurs
                  </h2>
                </div>
                <div className="space-y-4 text-gray-600">
                  <p>En utilisant nos services, les annonceurs s'engagent à :</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Fournir des créations publicitaires conformes à la législation française</li>
                    <li>Respecter les valeurs écologiques et solidaires d'Eau Gratuite</li>
                    <li>Ne pas promouvoir de contenus illégaux, offensants ou trompeurs</li>
                    <li>Respecter les droits de propriété intellectuelle</li>
                    <li>Payer les factures selon les modalités convenues</li>
                  </ul>
                  <p>
                    Nous nous réservons le droit de refuser toute campagne ne respectant pas 
                    ces conditions ou nos valeurs d'entreprise.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Responsabilités */}
            <Card className="p-8 border-0 shadow-lg">
              <CardContent className="p-0">
                <div className="flex items-center space-x-3 mb-6">
                  <Scale className="h-6 w-6 text-emerald-600" />
                  <h2 className="text-2xl font-bold text-gray-900">
                    4. Responsabilités et Garanties
                  </h2>
                </div>
                <div className="space-y-4 text-gray-600">
                  <p><strong>Eau Gratuite s'engage à :</strong></p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Distribuer les bouteilles selon les modalités convenues</li>
                    <li>Fournir des rapports transparents sur les campagnes</li>
                    <li>Reverser la part convenue à l'Association Ulker's</li>
                    <li>Maintenir la confidentialité des informations clients</li>
                  </ul>
                  
                  <p className="mt-6"><strong>Limitations de responsabilité :</strong></p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Nous ne garantissons pas un nombre exact de personnes touchées</li>
                    <li>Les conditions météorologiques peuvent affecter la distribution</li>
                    <li>Les performances dépendent de facteurs externes (événements, actualités)</li>
                    <li>Notre responsabilité est limitée au montant de la campagne</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Paiements */}
            <Card className="p-8 border-0 shadow-lg">
              <CardContent className="p-0">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  5. Conditions de Paiement
                </h2>
                <div className="space-y-4 text-gray-600">
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Paiement par virement bancaire ou chèque d'entreprise</li>
                    <li>Paiement intégral exigé avant le lancement de la production</li>
                    <li>La production ne démarre qu'après confirmation du paiement</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Résiliation */}
            <Card className="p-8 border-0 shadow-lg">
              <CardContent className="p-0">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  6. Résiliation
                </h2>
                <div className="space-y-4 text-gray-600">
                  <p>
                    Les contrats de campagne ne peuvent pas être résiliés une fois la commande confirmée 
                    et le paiement effectué.
                  </p>
                  <p>
                    En cas de manquement grave aux présentes conditions, nous nous réservons 
                    le droit de suspendre immédiatement les services.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Droit applicable */}
            <Card className="p-8 border-0 shadow-lg">
              <CardContent className="p-0">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  7. Droit Applicable
                </h2>
                <div className="space-y-4 text-gray-600">
                  <p>
                    Les présentes conditions sont soumises au droit français. 
                    Tout litige sera porté devant les tribunaux de Paris.
                  </p>
                  <p>
                    En cas de difficulté d'interprétation, la version française fait foi.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card className="p-8 border-0 shadow-lg bg-gradient-to-r from-sky-50 to-emerald-50">
              <CardContent className="p-0 text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Questions Juridiques ?
                </h2>
                <p className="text-gray-600 mb-6">
                  Notre équipe juridique est disponible pour clarifier ces conditions.
                </p>
                <div className="space-y-2">
                  <p className="font-semibold text-gray-900">Eau Gratuite</p>
                  <p className="text-gray-600">Email : contact@eaugratuite.io</p>
                  <p className="text-gray-600">Téléphone : 07 49 97 72 32</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;